import { Injectable } from "@angular/core";
import { combineLatest, Observable, of, ReplaySubject, Subject } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { Widget, WidgetSetting, WidgetUserFilter } from "src/app/classes";
import { IWidgetProviderService } from "src/app/interfaces";
import { WidgetService } from 'src/app/services/widget.service';
import { WidgetInnerView } from "src/app/widget-inner";
import { WidgetBarPosition } from "../widget-globals";
import { CustomChartView } from "./widget.component";

@Injectable()
export class WidgetComponentService implements IWidgetProviderService {

    private _widget = new ReplaySubject<Widget>(1);
    widget$ = this._widget.asObservable();
    widgetType$ = this._widget.pipe(switchMap(x => this._widgetService.getWidgetTypeById(x.widgettype, true)));

    contentType: string = null;
    chartViews: CustomChartView[] = [];
    iFrameViews: string[] = [];
    widgetInnerViews: WidgetInnerView[] = [];
    widgetBarPosition: number = WidgetBarPosition.Left;

    constructor(private _widgetService: WidgetService) { }

    addChartView(widgetSetting: WidgetSetting) {
        var fileName = "unrecognized filename";
        let array = widgetSetting.widgetsettingvalue.split("/");
        if (Array.isArray(array) && array.length > 0) fileName = array[array.length - 1];
        let view = {
            widgetsettingid: widgetSetting.widgetsettingid,
            widgetid: widgetSetting.widgetid,
            href: widgetSetting.widgetsettingvalue,
            name: fileName
        } as CustomChartView;
        this.chartViews.push(view);
        return view;
    }

    addIFrameView(href: string) {
        this.iFrameViews.push(href);
    }

    deleteUserFilter = (filter: WidgetUserFilter) => {
        let subject = new Subject();
        this._widgetService
            .deleteWidgetUserFilter(filter.filterid)
            .pipe(switchMap(() => this.widget$))
            .subscribe(
                (widget: Widget) => {
                    let index = widget.widgetuserfilters.indexOf(filter);
                    if (index > -1) widget.widgetuserfilters.splice(index, 1);
                    subject.next(filter);
                },
                (error) => subject.error(error),
                () => subject.complete()
            );
        return subject.asObservable();
    }

    deleteWidgetSetting = (setting: WidgetSetting) => {
        if(setting === null){
            console.error(`cannot delete widget setting: empty widget setting passed!`);
            return;
        }
        this._widgetService
            .deleteWidgetSetting(setting.widgetsettingid)
            .pipe(switchMap(() => this.widget$))
            .subscribe((widget: Widget) => {
                let index = widget.widgetsetting.indexOf(setting);
                if (index > -1) widget.widgetsetting.splice(index, 1);
                if(setting.widgetsettingdesc === 'custom-chart-view'){
                    index = this.chartViews.findIndex(x => x.widgetsettingid === setting.widgetsettingid);
                    if (index > -1) this.chartViews.splice(index, 1);
                } else if(setting.widgetsettingdesc === 'iframe-href'){
                    index = this.iFrameViews.findIndex(x => x === setting.widgetsettingvalue);
                    if (index > -1) this.iFrameViews.splice(index, 1);
                }                
            });
    }

    setSetting(setting: WidgetSetting): Observable<WidgetSetting> {
        return this.widget$
            .pipe(
                switchMap((widget: Widget) => combineLatest([of(widget), of(widget.widgetsetting.find(x => x.widgetsettingdesc === setting.widgetsettingdesc))])),
                switchMap(([widget, existingSetting]) => {
                    if (existingSetting) return this._widgetService.updateWidgetSetting({
                        'widgetid': existingSetting.widgetid,
                        'widgetsettingdesc': existingSetting.widgetsettingdesc,
                        'widgetsettingvalue': setting.widgetsettingvalue,
                        'widgetsettingid': existingSetting.widgetsettingid
                    }).pipe(tap(setting => existingSetting.widgetsettingvalue = setting.widgetsettingvalue));
                    else return this._widgetService.createWidgetSetting({
                        'widgetid': widget.widgetid,
                        'widgetsettingdesc': setting.widgetsettingdesc,
                        'widgetsettingvalue': setting.widgetsettingvalue
                    } as WidgetSetting).pipe(tap(setting => (widget as Widget).widgetsetting.push(setting)));
                })
            );
    }

    setWidget = (widget: Widget) => this._widget.next(widget);
    setWidgetInnerViews = (views: WidgetInnerView[]) => this.widgetInnerViews = views;

}